/* eslint no-param-reassign: 0 */
import {
  DirectoryGroupingConsts,
  ItemTypeConsts,
  ModuleConsts,
  UsersConsts,
} from 'client/util/Constants';
import {
  cloneDeep,
  difference,
  find,
  get,
  includes,
  isEqual,
} from 'lodash';
import { series } from 'async';
import CollectionApi from 'client/api/v2/CollectionApi';
import DateUtil from 'client/util/DateUtil';
import DefaultSystemUserModel from 'client/store/modules/models/DefaultSystemUserModel';
import JobApi from 'client/api/v2/JobApi';
import LdapServerApi from 'client/api/v2/LdapServerApi';
import ModelMixin from 'client/store/modules/models/mixins/ModelMixin';
import SystemApi from 'client/api/v2/SystemApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import SystemUserPublicKeysApi from 'client/api/v2/SystemUserPublicKeysApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Vue from 'vue';

const {
  application,
  directory,
  ldap,
  radiusServer,
  system,
} = ItemTypeConsts;
const { groupTypes, memberTypes } = DirectoryGroupingConsts;
const { activated, suspended } = ModuleConsts.systemUser.accountStates;

const suspendedText = 'suspended';

const directoryCollection = 'DirectoryCollection';
const systemCollection = 'SystemCollection';
const systemUserCollection = 'SystemUserCollection';
const systemUserWebAuthnKeysModel = 'SystemUserWebAuthnKeysModel';

/**
 * The SystemUserModel Vuex module provides an interface to the System User
 * item being created or updated in the UserAsidePanel.
 * @todo
 * 1. Extract the common functionality present here to either a mixin or base
 *    class. Eventually, when a second Aside Panel is refactored to use
 *    Vue/Vuex, we'll want to have a consistent interface to the Vuex Model
 *    modules used by the AsidePanel component, and many actions, such as
      saveItem() can be implemented generically given an Api class instance.
 */
export const createSystemUserModel = () => ({
  namespaced: true,

  state: {
    bindings: {},

    item: DefaultSystemUserModel(),

    // Explicitly determine whether an item is being created or updated. This
    // is useful so that the UI can determine the _precise_ state of an item.
    itemState: {
      create: undefined,
      update: undefined,
    },

    // Keep track of initial item state so the Aside Panel can be "stateful"
    previousItem: DefaultSystemUserModel(),
    previousPublicKeys: [],
    publicKeys: [],
    scheduledStateChangesForUser: [],
  },

  actions: {
    ...ModelMixin.actions,

    fetchItem(context) {
      const { id } = context.getters;

      return id
        ? context.dispatch('fetchExistingUser')
        : context.dispatch('fetchNewUser');
    },

    fetchExistingUser(context) {
      const { id } = context.getters;

      return SystemUserApi
        .get(id)
        .then((item) => {
          // Clone the item so that any changes to non-primitive type values,
          // such as an array of phone numbers, are _not_ accidentally made
          // to the previous item when the item is mutated.
          context.commit('SET_ITEM', cloneDeep(item));
          context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));

          context.dispatch('fetchBindings');

          const payload = {
            id,
            displayName: context.getters.displayName,
            username: context.state.item.username,
          };
          const root = true;

          context.dispatch(`${systemUserWebAuthnKeysModel}/updateItem`, payload, { root });
          context.dispatch(`${systemUserWebAuthnKeysModel}/updatePreviousItem`, payload, { root });
          context.dispatch(`${systemUserWebAuthnKeysModel}/fetchItem`, { userId: id }, { root: true });

          if (item.state === suspended) {
            context.commit('SET_RESTRICTED_FIELDS', { password: { id: '', type: suspendedText } });
            context.commit('SET_PREVIOUS_RESTRICTED_FIELDS', { password: { id: '', type: suspendedText } });
          }
        });
    },

    /**
     * Set the correct initial state for a new user
     */
    fetchNewUser(context) {
      const { newSystemUserStateDefaults } = context.rootState.SettingsModule;

      const state = get(newSystemUserStateDefaults, 'manualEntry');
      const newItem = {
        ...context.state.item,
        state,
      };

      context.commit('SET_ITEM', newItem);
      context.commit('SET_PREVIOUS_ITEM', newItem);

      return Promise.resolve();
    },

    /**
     * Determine the list of devices that are associated (directly bound) to a system user.
     *
     * The max `limit` on this endpoint is 100. For the time being, we are not fetching
     * any devices beyond the limit and will wait for customer and product feedback
     * on whether we should fetch _all_ devices.
     */
    async fetchDeviceAssociations(context) {
      const { item } = context.getters;
      let errorOccurred = false;
      let searchResults = {
        data: {
          results: [],
          totalCount: 0,
        },
      };
      let totalCountOfDevices = 0;

      if (item) {
        try {
          const { results, totalCount } = await SystemUserApi.getDeviceAssociations(item._id);
          if (results.length) {
            searchResults = await SystemApi.search({
              fields: ['displayName'],
              filter: { or: results.map(d => ({ _id: d.to.id })) },
            });

            totalCountOfDevices = totalCount;
          }
        } catch {
          errorOccurred = true;
        }
      }

      return Promise.resolve({ data: searchResults.data, errorOccurred, totalCountOfDevices });
    },

    /**
     * Determine if the user is directly bound to any directories (except LDAP)
     */
    async fetchDirectoryAssociations(context) {
      const { item } = context.getters;
      const associations = [];
      let errorOccurred = false;

      const getItem = `${directoryCollection}/getItem`;
      const isItemDirectlyBound = `${directoryCollection}/isItemDirectlyBound`;

      if (item) {
        try {
          const { results } = await SystemUserApi.getBoundDirectories(item._id, {});

          if (results.length) {
            results.forEach((result) => {
              if (result.type !== ldap
                && context.rootGetters[isItemDirectlyBound](result.id)) {
                // Push the directory item
                associations.push(context.rootGetters[getItem](result.id));
              }
            });
          }
        } catch {
          errorOccurred = true;
        }
      }

      return Promise.resolve({ associations, errorOccurred });
    },

    /**
     * Determine if the user is associated with the LDAP server
     * Note: Assumes only one LDAP server; if that ever changes, this code breaks
     */
    async fetchLdapAssociation(context, ldapId) {
      const { item } = context.getters;

      let found = false;
      let errorOccurred = false;

      if (item && ldapId) {
        try {
          const associations = await LdapServerApi.getAssociations(ldapId);
          found = !!associations.find(association => association.to.id === item._id);
        } catch {
          errorOccurred = true;
        }
      }

      return Promise.resolve({ found, errorOccurred });
    },

    /**
     * Determine the list of user groups that are associated (directly bound) to a system user.
     *
     * The `limit` on this endpoint is 100. For the time being, we are not fetching
     * any user groups beyond the limit because it could present inaccurate data when
     * a user is associated with > 100 user groups
     * (according to comments here -
     * https://github.com/TheJumpCloud/jumpcloud-admin-portal/blob/99dd8f603161e0994f6474dcbbcd9ea59528dff4/client/systemusers/UserHighlights/UserHighlightsApplicationsModal.vue#L139).
     */
    async fetchUserGroupAssociations(context) {
      const { item } = context.getters;
      let data = [];
      let errorOccurred = false;
      let totalCountOfUserGroups = 0;

      if (item) {
        try {
          const {
            results,
            totalCount,
          } = await SystemUserApi.getBoundUserGroups(item._id, { limit: 100 });

          if (results.length) {
            const groupIds = results.map(group => group.id);
            const groups = await Promise.all(groupIds.map(id => UserGroupApi.get(id)));

            // Get id and display name
            data = groups.map(group => ({
              id: group.id,
              name: group.name,
            }));

            totalCountOfUserGroups = totalCount;
          }
        } catch {
          errorOccurred = true;
        }
      }

      return Promise.resolve({ data, errorOccurred, totalCountOfUserGroups });
    },

    async fetchManagerUser(context) {
      const { item } = context.getters;

      if (!item || !item.manager) {
        return undefined;
      }

      const data = await SystemUserApi.get(item.manager);
      if (!data) {
        return undefined;
      }

      const {
        _id: value,
        email: subtitle,
        firstname = '',
        lastname = '',
        username,
      } = data;

      let title = '';

      if (firstname || lastname) {
        title = [lastname, firstname].filter(v => !!v).join(',\u0020');
      } else {
        title += username;
      }

      return { value, title, subtitle };
    },

    /**
     * Check to see if the user is bound to any applications, diectories, radius servers, or
     * systems; saving the number of bindings in the state. Later, the hasBindings getter will
     * query the number of bindings and if all are 0 then the username field of an existing user
     * can be changed in the User Aside.
     */
    fetchBindings({ getters, commit }) {
      const { id } = getters;

      const applicationRequest = new CollectionApi(`${UsersConsts.APIPaths.users}${id}/applications`, {});
      const directoryRequest = new CollectionApi(`${UsersConsts.APIPaths.users}${id}/directories`, {});
      const radiusServerRequest = new CollectionApi(`${UsersConsts.APIPaths.users}${id}/radiusservers`, {});
      const systemRequest = new CollectionApi(`${UsersConsts.APIPaths.users}${id}/systems`, {});

      const bindings = {};

      return Promise
        .all([
          applicationRequest
            .fetchTotalCount()
            .then((totalCount) => { bindings[application] = totalCount; }),
          directoryRequest
            .fetchTotalCount()
            .then((totalCount) => { bindings[directory] = totalCount; }),
          radiusServerRequest
            .fetchTotalCount()
            .then((totalCount) => { bindings[radiusServer] = totalCount; }),
          systemRequest
            .fetchTotalCount()
            .then((totalCount) => { bindings[system] = totalCount; }),
        ])
        .then(() => commit('SET_BINDINGS', bindings))
        .catch(() => { /* NOOP */ });
    },

    fetchPublicKeys(context) {
      const { id } = context.getters;
      SystemUserPublicKeysApi.id = id;

      return SystemUserPublicKeysApi
        .fetchAll()
        .then((publicKeys) => {
          context.commit('SET_PUBLIC_KEYS', cloneDeep(publicKeys));
          context.commit('SET_PREVIOUS_PUBLIC_KEYS', cloneDeep(publicKeys));
        });
    },

    fetchScheduledStateChanges(context) {
      const { id } = context.getters;

      return JobApi
        .getScheduledStateChanges(id)
        .then((changes) => {
          const compareDates = (date, comparisonDate) => {
            if (DateUtil.isBeforeDate(date.scheduledDate, comparisonDate.scheduledDate)) {
              return -1;
            }
            if (DateUtil.isAfterDate(date.scheduledDate, comparisonDate.scheduledDate)) {
              return 1;
            }
            return 0;
          };

          // Get the list of scheduled events for the user, and sort the list
          // so that the earliest scheduled job is first in the array
          changes.sort(compareDates);

          context.commit('SET_SCHEDULED_STATE_CHANGES', changes);
        });
    },

    saveItem(context, shouldSendEmail) {
      const { getters } = context;
      const expireAfterCreation = getters.isItemBeingCreated && getters.item.password_expired;
      let errMsg;

      const handleSaveItem = async (item) => {
        // Expire the user immediately after creation
        if (expireAfterCreation) {
          try {
            await context.dispatch('expireUser', item._id);
          } catch (error) {
            errMsg = error;
          }
        }

        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });

        // Ensure that the created or updated item is updated in the User Main
        // Panel by passing the item to the SystemUserCollection so it can be
        // synced in the store immediately.
        context.dispatch('SystemUserCollection/refreshCollection', { item }, { root: true });

        if (errMsg) {
          throw errMsg;
        }

        return item;
      };

      return getters.isItemBeingCreated
        ? SystemUserApi.create(getters.item, shouldSendEmail).then(handleSaveItem)
        : SystemUserApi.update(getters.item).then(handleSaveItem);
    },

    savePublicKeys(context) {
      return new Promise((resolve, reject) => {
        const { state } = context;
        const { publicKeys } = state;
        const { previousPublicKeys } = state;
        const publicKeyIds = publicKeys.map(k => k._id || k.tempId);
        const previousPublicKeyIds = previousPublicKeys.map(k => k._id);

        const createdPublicKeys = difference(publicKeyIds, previousPublicKeyIds)
          .map(id => find(publicKeys, pk => pk._id === id || pk.tempId === id));

        const deletedPublicKeys = difference(previousPublicKeyIds, publicKeyIds)
          .map(id => find(previousPublicKeys, ppk => ppk._id === id));

        const updatedPublicKeys = previousPublicKeys.reduce((acc, previousPublicKey) => {
          // If the previous key is deleted then just return
          if (includes(deletedPublicKeys.map(k => k._id), previousPublicKey._id)) {
            return acc;
          }

          // If the previous key is not the same as the new key then add the
          // new key to the accumulator
          const currentPublicKey = find(publicKeys, k => k._id === previousPublicKey._id);

          if (!isEqual(currentPublicKey, previousPublicKey)) {
            acc.push(currentPublicKey);
          }

          return acc;
        }, []);

        const tasks = [];

        createdPublicKeys.forEach((createdPublicKey) => {
          tasks.push((cb) => {
            const publicKey = { ...createdPublicKey };
            delete publicKey.tempId;

            SystemUserPublicKeysApi
              .create(publicKey)
              .then(() => cb())
              .catch(err => cb(err));
          });
        });

        deletedPublicKeys.forEach((deletedPublicKey) => {
          tasks.push((cb) => {
            SystemUserPublicKeysApi
              .delete(deletedPublicKey._id)
              .then(() => cb())
              .catch(err => cb(err));
          });
        });

        updatedPublicKeys.forEach((updatedPublicKey) => {
          tasks.push((cb) => {
            SystemUserPublicKeysApi
              .update(updatedPublicKey)
              .then(() => cb())
              .catch(err => cb(err));
          });
        });

        if (tasks.length === 0) {
          return resolve();
        }

        // NOTE: The series() method from the async library is used here to
        // ensure that the API requests are one one at a time because it has
        // been seen that the server will return 503 errors if they're run in
        // parallel. Presumably this is becuase they all operate on the same
        // dataset in Mongo.
        return series(tasks, (err) => {
          if (err) {
            reject(err);
          } else {
            // Fetch a fresh copy of the public keys so that the copy of the
            // public keys and previous keys in the state are the same and
            // reflect the current state of the public keys on the server.
            context
              .dispatch('fetchPublicKeys')
              .then(() => resolve())
              .catch(err2 => reject(err2));
          }
        });
      });
    },

    bindUserToUserGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.user,
        targetType: groupTypes.userGroup,
      });
    },

    bindUserToSystems(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.user,
        targetType: memberTypes.system,
      });
    },

    /**
     * Bind/unbind a directory to/from a user by making a request to POST
     * ${UsersConsts.APIPaths.users}{userId}/associations for each selected/unselected
     * directory. Note that this differs from binding the user to systems or
     * user groups because each directory is a different type of object, which
     * requires specifying its unique targetType in the payload. Hence a
     * separate request must be made for each.
     * @see https://docs.jumpcloud.com/2.0/users/manage-associations-of-a-user
     */
    bindUserToDirectories(context) {
      const selectedTargetIds = context.getters.getTargetsToBind(ItemTypeConsts.directory);
      const unselectedTargetIds = context.getters.getTargetsToUnbind(ItemTypeConsts.directory);

      const parentType = memberTypes.user;
      const parentId = context.getters.id;
      const payloads = [];

      selectedTargetIds.forEach((selectedTargetId) => {
        // Use `getBoundItem` here as `getItem` does not work when creating staged users
        const item = context.rootGetters[`${directoryCollection}/getBoundItem`](selectedTargetId);

        payloads.push({
          parentType,
          parentId,
          targetType: item.type,
          selectedTargetIds: [item.id],
        });
      });

      unselectedTargetIds.forEach((unselectedTargetId) => {
        const item = context.rootGetters[`${directoryCollection}/getItem`](unselectedTargetId);

        payloads.push({
          parentType,
          parentId,
          targetType: item.type,
          unselectedTargetIds: [item.id],
        });
      });

      if (payloads.length === 0) {
        return Promise.resolve();
      }

      const tasks = payloads.map(payload => context.dispatch(
        'DirectoryGroupingModule/bindAssociations',
        payload,
        { root: true },
      ));

      return Promise.all(tasks);
    },

    bindSudoStatus(context) {
      const { id } = context.getters;
      const targetsToUpdate = context.getters.getSudoersToUpdate;

      const payload = {
        parentType: memberTypes.user,
        parentId: id,
        targetType: memberTypes.system,
        targetsToUpdate,
      };

      return context.dispatch(
        'DirectoryGroupingModule/bindAttributes',
        payload,
        { root: true },
      );
    },

    /**
     * Activate user's password. This is needed for async action of setting password
     * through new password modal dialog. So the user form acts accordingly.
     */
    activatePassword(context, { expirationDate }) {
      const payload = {
        activated: true,
        password_expiration_date: expirationDate,
        password_expired: false,
      };

      // update both item and previousItem models
      context.dispatch('updateItem', payload);
      context.dispatch('updatePreviousItem', payload);
    },

    activateUser(context) {
      const payload = {
        state: activated,
        suspended: false, // Will be deprecated in the future
      };

      return context.dispatch('performUserAction', payload);
    },

    /**
     * @param { Boolean } email Whether or not a welcome email should be sent
     * Empty email param SENDS the email, in the future, it can also be a string containing
     * the recipient's email address
     */
    activateWithEmail(context, email) {
      const { id } = context.getters;
      return SystemUserApi.activate(id, email);
    },

    expireUser(context, id) {
      return SystemUserApi.expire(id).then(() => {
        // Expired users can't have password_never_expires set to true so reset here
        const payload = {
          password_expired: true,
          password_expiration_date: new Date().toISOString(),
          password_never_expires: false,
        };

        // Update the system user model locally
        context.dispatch('updateItem', payload);
        context.dispatch('updatePreviousItem', payload);

        // Update the SystemUserCollection
        context.commit(`${systemUserCollection}/UPDATE_ITEM`, {
          id,
          attributes: payload,
        }, { root: true });
      });
    },

    /**
     * @description Suspend or Activate a user as specified in the payload
     */
    performUserAction(context, payload) {
      const { item } = context.getters;
      const { state } = item; // Save in case of failure

      return SystemUserApi.suspend(item._id, payload.state)
        .then(async () => {
          // On success, update the flag on the item model
          context.dispatch('updateItem', payload);
          context.dispatch('updatePreviousItem', payload);

          const payload2 = { password: { id: '', type: suspendedText } };

          if (payload.state === suspended) {
            // If the user is being suspended, restrict the admin's ability to edit password
            context.commit('SET_RESTRICTED_FIELDS', payload2);
            context.commit('SET_PREVIOUS_RESTRICTED_FIELDS', payload2);
          } else {
            // If the user is being activated, re-fetch the item to get the new mfa values
            // and then remove the edit restriction on the password field
            await context.dispatch('fetchItem');
            context.commit('UNSET_RESTRICTED_FIELDS', payload2);
            context.commit('UNSET_PREVIOUS_RESTRICTED_FIELDS', payload2);
          }

          // Update the SystemUserCollection
          context.commit(`${systemUserCollection}/UPDATE_ITEM`, {
            id: item._id,
            attributes: {
              // The suspended field may be deprecated in the future,
              suspended: payload.state === suspended,
              state: payload.state,
            },
            mfa: item.mfa, // In case these values changed with the action
          }, { root: true });
        })
        .catch((error) => {
          // If the update failed, return `previousItem` to its former state
          const payload3 = {
            // The suspended field may be deprecated in the future,
            suspended: state === suspended,
            state,
          };
          context.dispatch('updatePreviousItem', payload3);
          throw error;
        });
    },

    resetItem({ dispatch, commit }) {
      dispatch(`${systemUserWebAuthnKeysModel}/resetItem`, null, { root: true });
      commit('RESET_ITEM');
    },

    /**
     * Sends either activation email or a reset password
     * request email depending on the user whether the user is activated
     * @param {String} activationEmail Alt activation Email
     * only works for activation email
     */
    sendEmail({ getters }, activationEmail) {
      const { item } = getters;
      return SystemUserApi.sendSingleEmail(item._id, activationEmail);
    },

    setPublicKeys({ commit }, publicKeys) {
      commit('SET_PUBLIC_KEYS', publicKeys);
    },

    suspendUser(context) {
      const payload = {
        state: suspended,
        suspended: true, // Will be deprecated in the future
      };
      return context.dispatch('performUserAction', payload);
    },

    unlockUser({ commit, dispatch, getters }) {
      const { id } = getters;
      return SystemUserApi.unlock(id).then(() => {
        // Update the system user model locally (user in aside panel)
        const payload = {
          account_locked: false,
          badLoginAttempts: 0,
        };
        dispatch('updateItem', payload);
        dispatch('updatePreviousItem', payload);

        // Update the SystemUserCollection
        commit(`${systemUserCollection}/UPDATE_ITEM`, {
          id,
          attributes: {
            account_locked: false,
            badLoginAttempts: 0,
          },
        }, { root: true });
      });
    },

    /**
     * If the user's password is being forced to expire then enable the org setting
     * to allow this if it isn't already.
     *
     * @see https://jumpcloud.atlassian.net/browse/DE-1311 &&
     * @see https://jumpcloud.atlassian.net/browse/DE-1319
     */
    async updatePasswordPolicy({ dispatch, rootState, state }) {
      const { item, previousItem } = state;
      const { passwordPolicy } = rootState.SettingsModule;

      const shouldUpdateSetting = !passwordPolicy.enableDaysAfterExpirationToSelfRecover
        && !previousItem.password_expired
        && (item.password_expired !== previousItem.password_expired);

      if (shouldUpdateSetting) {
        const payload = { enableDaysAfterExpirationToSelfRecover: true };

        try {
          await dispatch('SettingsModel/updatePasswordPolicy', payload, { root: true });
          await dispatch('SettingsModel/saveItem', null, { root: true });
        } catch (err) {
          if (err instanceof Error) {
            throw err;
          }
          throw new Error('Unable to update the password policy for the organization.');
        }
      }
    },
  },

  getters: {
    ...ModelMixin.getters,

    /**
     * @todo This is duplicated from the CollectionModule and should be moved
     * to a mixin for use in both Vuex modules.
     */
    displayName(state) {
      const firstname = get(state.item, 'firstname', '');
      const lastname = get(state.item, 'lastname', '');
      const username = get(state.item, 'username', '').trim();
      const displayName = `${firstname} ${lastname}`.trim();

      return displayName || username || '';
    },

    getSudoersToUpdate(state, getters, rootState, rootGetters) {
      return rootGetters[`${systemCollection}/getSudoersToUpdate`];
    },

    getSystemBindingChanges(state, getters) {
      const systemsToBind = getters.getTargetsToBind(memberTypes.system);
      const systemsToUnbind = getters.getTargetsToUnbind(memberTypes.system);

      return { binds: systemsToBind, unbinds: systemsToUnbind };
    },

    /**
     * Checks to see if the user is bound to any systems, directories, applications or Radius.
     * Used to determine whether a user's username is mutable or not.
     */
    hasBindings(state) {
      const { bindings } = state;

      return bindings[application] > 0
        || bindings[directory] > 0
        || bindings[radiusServer] > 0
        || bindings[system] > 0;
    },

    hasSystemBindings(state) {
      const { bindings } = state;
      return bindings[system] > 0;
    },

    haveSystemBindingsChanged(state, getters) {
      const systemsToBind = getters.getTargetsToBind(memberTypes.system);
      const systemsToUnbind = getters.getTargetsToUnbind(memberTypes.system);

      return !!(systemsToBind.length || systemsToUnbind.length);
    },

    haveAssociationsChanged(state, getters) {
      const userGroupsToBind = getters.getTargetsToBind(groupTypes.userGroup);
      const userGroupsToUnbind = getters.getTargetsToUnbind(groupTypes.userGroup);
      const systemsToBind = getters.getTargetsToBind(memberTypes.system);
      const systemsToUnbind = getters.getTargetsToUnbind(memberTypes.system);
      const directoriesToBind = getters.getTargetsToBind(ItemTypeConsts.directory);
      const directoriesToUnbind = getters.getTargetsToUnbind(ItemTypeConsts.directory);
      const sudoersToUpdate = getters.getSudoersToUpdate;

      if (userGroupsToBind.length
          || userGroupsToUnbind.length
          || systemsToBind.length
          || systemsToUnbind.length
          || directoriesToBind.length
          || directoriesToUnbind.length
          || Object.keys(sudoersToUpdate).length) {
        return true;
      }

      return false;
    },

    havePublicKeysChanged(state) {
      const previousPublicKeys = JSON.parse(JSON.stringify(state.previousPublicKeys));
      const publicKeys = JSON.parse(JSON.stringify(state.publicKeys));
      return !isEqual(previousPublicKeys, publicKeys);
    },

    /**
     * Legacy items that reside in Mongo use the _id field while newer items
     * that live in Postgres, such as Policies and Groups, use the id field.
     */
    idField() {
      return '_id';
    },

    /**
     * Allow components to determine when a field is restricted, i.e. cannot be
     * modified in the UI. Users that come from Active Directory will have a
     * restricted_fields property that is checked first. If the user does not
     * have a restricted_fields property, but is externally managed, then check
     * to see if the field is in our list of known restricted fields. Otherwise
     * return false, the field is not restricted.
     */
    isRestrictedField(state = { item: {} }) {
      return ((field) => {
        if (state.item.restricted_fields && state.item.restricted_fields[field]) {
          return true;
        }

        return false;
      });
    },
  },

  mutations: {
    ...ModelMixin.mutations,

    /**
     * @todo Rename this method RESET_STATE because it resets the state, not
     * just the item.
     */
    RESET_ITEM(state) {
      state.bindings = {};
      state.item = DefaultSystemUserModel();
      state.itemState.create = undefined;
      state.itemState.update = undefined;
      state.previousItem = DefaultSystemUserModel();
      state.previousPublicKeys = [];
      state.publicKeys = [];
      state.scheduledStateChangesForUser = [];
    },

    SET_BINDINGS(state, bindings) {
      Vue.set(state, 'bindings', bindings);
    },

    SET_PREVIOUS_PUBLIC_KEYS(state, publicKeys) {
      Vue.set(state, 'previousPublicKeys', publicKeys);
    },

    SET_PUBLIC_KEYS(state, publicKeys) {
      Vue.set(state, 'publicKeys', publicKeys);
    },

    SET_PREVIOUS_RESTRICTED_FIELDS(state, fields) {
      Vue.set(state.previousItem, 'restricted_fields', { ...state.previousItem.restricted_fields, ...fields });
    },

    SET_RESTRICTED_FIELDS(state, fields) {
      Vue.set(state.item, 'restricted_fields', { ...state.item.restricted_fields, ...fields });
    },

    SET_SCHEDULED_STATE_CHANGES(state, changes = []) {
      Vue.set(state, 'scheduledStateChangesForUser', changes);
    },

    UNSET_PREVIOUS_RESTRICTED_FIELDS(state, fields) {
      Object.keys(fields).forEach((field) => {
        Vue.delete(state.previousItem.restricted_fields, field);
      });
    },

    UNSET_RESTRICTED_FIELDS(state, fields) {
      Object.keys(fields).forEach((field) => {
        Vue.delete(state.item.restricted_fields, field);
      });
    },
  },
});

export default createSystemUserModel();
